import React, { useEffect, useState, useRef } from 'react';
import heroImage from '../assets/hero_dashboard.png';

const Hero = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentWord, setCurrentWord] = useState(0);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);
    const cardRef = useRef(null);

    const rotatingWords = ['Innovation', 'Excellence', 'Success', 'Growth'];

    useEffect(() => {
        setIsVisible(true);

        // Rotating word animation
        const wordInterval = setInterval(() => {
            setCurrentWord((prev) => (prev + 1) % rotatingWords.length);
        }, 3000);

        return () => clearInterval(wordInterval);
    }, []);

    const scrollToSection = (href) => {
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // 3D Tilt effect handler
    const handleMouseMove = (e) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 10;
        const rotateY = (centerX - x) / 10;

        setMousePosition({
            x: (x / rect.width) * 100,
            y: (y / rect.height) * 100,
            rotateX,
            rotateY
        });
    };

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => {
        setIsHovering(false);
        setMousePosition({ x: 50, y: 50, rotateX: 0, rotateY: 0 });
    };

    const stats = [
        { number: '500+', label: 'Projects Delivered', icon: '🚀' },
        { number: '200+', label: 'Happy Clients', icon: '❤️' },
        { number: '8+', label: 'Years Experience', icon: '⚡' },
        { number: '50+', label: 'Tech Experts', icon: '👥' }
    ];

    const features = [
        { icon: '🎯', text: 'Data-Driven Solutions' },
        { icon: '⚡', text: 'Lightning Fast Delivery' },
        { icon: '🔒', text: 'Enterprise Security' }
    ];

    return (
        <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
            {/* Dynamic Animated Background */}
            <div className="absolute inset-0">
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20 animate-gradient"></div>

                {/* Floating Orbs */}
                <div className="absolute top-20 left-[10%] w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
                <div className="absolute bottom-20 right-[10%] w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
                <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(96, 165, 250, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(96, 165, 250, 0.3) 1px, transparent 1px)',
                        backgroundSize: '50px 50px'
                    }}>
                </div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 container-custom py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left Column - Text Content */}
                    <div className={`text-center lg:text-left transition-all duration-1000 ${isVisible ? 'animate-slideInLeft' : 'opacity-0'}`}>
                        {/* Badge/Label */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6">
                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                            <span className="text-white/90 text-sm font-medium">Your Technology Partner</span>
                        </div>

                        {/* Main Headline with Rotating Word */}
                        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight">
                            <span className="text-white">Empowering Your</span>
                            <span className="block mt-2">
                                <span className="text-white">Digital </span>
                                <span className="relative inline-block">
                                    <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-pulse">
                                        {rotatingWords[currentWord]}
                                    </span>
                                    <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full"></span>
                                </span>
                            </span>
                        </h1>

                        {/* Subheadline */}
                        <p className="text-lg md:text-xl lg:text-2xl mb-8 text-gray-300 font-light leading-relaxed max-w-2xl mx-auto lg:mx-0">
                            Transform your business with cutting-edge software solutions.
                            <span className="text-white font-medium"> We build, you grow.</span>
                        </p>

                        {/* Feature Pills */}
                        <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-8">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                                >
                                    <span className="text-xl">{feature.icon}</span>
                                    <span className="text-white/90 text-sm font-medium">{feature.text}</span>
                                </div>
                            ))}
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                            <button
                                onClick={() => scrollToSection('#contact')}
                                className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/50 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-400/50"
                            >
                                <span className="relative z-10 flex items-center justify-center gap-2">
                                    Get Started Free
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                                    </svg>
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            </button>

                            <button
                                onClick={() => scrollToSection('#projects')}
                                className="group bg-white/10 backdrop-blur-md border-2 border-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:bg-white/20 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-white/30"
                            >
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z"></path>
                                    </svg>
                                    Watch Demo
                                </span>
                            </button>
                        </div>

                        {/* Social Proof */}
                        <div className="flex items-center gap-4 justify-center lg:justify-start">
                            <div className="flex -space-x-3">
                                {[1, 2, 3, 4].map((i) => (
                                    <div
                                        key={i}
                                        className="w-10 h-10 rounded-full border-2 border-slate-900 bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold"
                                    >
                                        {i}
                                    </div>
                                ))}
                            </div>
                            <div className="text-left">
                                <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-white/70 text-xs mt-1"><span className="font-semibold text-white">200+</span> happy clients</p>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Interactive 3D Visual */}
                    <div className={`mt-12 lg:mt-0 relative transition-all duration-1000 ${isVisible ? 'animate-slideInRight' : 'opacity-0'}`}>
                        <div className="relative w-full max-w-lg mx-auto lg:max-w-none" style={{ perspective: '1000px' }}>
                            {/* Glowing Background - follows mouse */}
                            <div
                                className="absolute inset-0 rounded-3xl blur-3xl transition-all duration-300"
                                style={{
                                    background: isHovering
                                        ? `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(6, 182, 212, 0.4), rgba(139, 92, 246, 0.2), transparent 70%)`
                                        : 'radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.2), rgba(139, 92, 246, 0.1), transparent 70%)'
                                }}
                            ></div>

                            {/* 3D Interactive Card */}
                            <div
                                ref={cardRef}
                                onMouseMove={handleMouseMove}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                className="relative rounded-2xl overflow-hidden cursor-pointer"
                                style={{
                                    transform: isHovering
                                        ? `rotateX(${mousePosition.rotateX}deg) rotateY(${mousePosition.rotateY}deg) scale3d(1.02, 1.02, 1.02)`
                                        : 'rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
                                    transformStyle: 'preserve-3d',
                                    transition: isHovering ? 'transform 0.1s ease-out' : 'transform 0.5s ease-out'
                                }}
                            >
                                {/* Animated Border Glow */}
                                <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-cyan-500 via-purple-500 to-blue-500 opacity-75"
                                    style={{
                                        background: isHovering
                                            ? `conic-gradient(from ${Date.now() / 10 % 360}deg, #06b6d4, #8b5cf6, #3b82f6, #06b6d4)`
                                            : 'linear-gradient(135deg, rgba(6, 182, 212, 0.5), rgba(139, 92, 246, 0.5), rgba(59, 130, 246, 0.5))',
                                        animation: 'spin 3s linear infinite'
                                    }}
                                ></div>

                                {/* Inner Container */}
                                <div className="relative m-[2px] rounded-2xl bg-slate-900/90 backdrop-blur-xl p-4 overflow-hidden">
                                    {/* Tech Grid Overlay */}
                                    <div
                                        className="absolute inset-0 opacity-20 pointer-events-none"
                                        style={{
                                            backgroundImage: `
                                                linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px),
                                                linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px)
                                            `,
                                            backgroundSize: '20px 20px',
                                            maskImage: isHovering
                                                ? `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, black, transparent 60%)`
                                                : 'none'
                                        }}
                                    ></div>

                                    {/* Spotlight Effect */}
                                    <div
                                        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                                        style={{
                                            background: isHovering
                                                ? `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.15), transparent 40%)`
                                                : 'none',
                                            opacity: isHovering ? 1 : 0
                                        }}
                                    ></div>

                                    {/* Scan Line Effect */}
                                    <div
                                        className="absolute inset-0 pointer-events-none overflow-hidden"
                                        style={{
                                            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(6, 182, 212, 0.03) 2px, rgba(6, 182, 212, 0.03) 4px)',
                                            animation: isHovering ? 'scanline 2s linear infinite' : 'none'
                                        }}
                                    ></div>

                                    {/* Main Image with 3D depth */}
                                    <img
                                        src={heroImage}
                                        alt="Ascali Digital Solutions"
                                        className="relative w-full h-auto object-contain rounded-xl shadow-2xl"
                                        style={{
                                            transform: isHovering ? 'translateZ(30px)' : 'translateZ(0)',
                                            transition: 'transform 0.3s ease-out'
                                        }}
                                    />

                                    {/* Floating Data Points */}
                                    {isHovering && (
                                        <>
                                            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 backdrop-blur-sm rounded-full border border-cyan-400/30 animate-pulse"
                                                style={{ transform: 'translateZ(50px)' }}>
                                                <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                                                <span className="text-cyan-300 text-xs font-mono">LIVE</span>
                                            </div>
                                            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-purple-500/20 backdrop-blur-sm rounded-full border border-purple-400/30"
                                                style={{ transform: 'translateZ(50px)' }}>
                                                <span className="text-purple-300 text-xs font-mono">v2.0.4</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Floating Info Cards with 3D effect */}
                            <div
                                className="absolute -top-6 -right-6 bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 shadow-xl hidden md:block"
                                style={{
                                    transform: isHovering
                                        ? `translateZ(60px) rotateX(${mousePosition.rotateX * 0.5}deg) rotateY(${mousePosition.rotateY * 0.5}deg)`
                                        : 'translateZ(0)',
                                    transition: 'transform 0.3s ease-out',
                                    animation: 'float 3s ease-in-out infinite'
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold">99.9%</div>
                                        <div className="text-white/70 text-xs">Uptime</div>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 shadow-xl hidden md:block"
                                style={{
                                    transform: isHovering
                                        ? `translateZ(60px) rotateX(${mousePosition.rotateX * 0.5}deg) rotateY(${mousePosition.rotateY * 0.5}deg)`
                                        : 'translateZ(0)',
                                    transition: 'transform 0.3s ease-out',
                                    animation: 'float 3s ease-in-out infinite',
                                    animationDelay: '1s'
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-lg flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold">Fast</div>
                                        <div className="text-white/70 text-xs">Delivery</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Section - Enhanced */}
                <div className={`mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className="group relative bg-white/5 backdrop-blur-sm p-6 rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 cursor-default"
                        >
                            <div className="text-center">
                                <div className="text-4xl mb-2">{stat.icon}</div>
                                <div className="text-3xl md:text-4xl font-bold text-white mb-2 font-display group-hover:scale-110 transition-transform">
                                    {stat.number}
                                </div>
                                <div className="text-sm text-gray-300">
                                    {stat.label}
                                </div>
                            </div>
                            {/* Hover Glow Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-blue-500/0 to-purple-500/0 group-hover:from-cyan-500/10 group-hover:via-blue-500/10 group-hover:to-purple-500/10 rounded-2xl transition-all duration-300"></div>
                        </div>
                    ))}
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <div className="flex flex-col items-center gap-2">
                        <span className="text-white/50 text-xs uppercase tracking-wider">Scroll</span>
                        <svg className="w-6 h-6 text-white/70" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                        </svg>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
